import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import {
  ClipboardList, CheckCircle2, Clock, Truck, Package, DollarSign,
  GraduationCap, Users, TrendingUp, AlertTriangle, ArrowUpRight, Loader2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';

const STATUS_COLORS = {
  Completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Scheduled: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'In Progress': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Pending: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  Paid: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

export default function Dashboard() {
  const ctx = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getDashboard().then(d => { setStats(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
    </div>
  );

  if (!stats) return <div className="text-center py-12">Failed to load dashboard data</div>;

  const kpis = [
    { label: 'Total Reservations', value: stats.total_reservations, icon: ClipboardList, color: 'blue', change: '+12%' },
    { label: 'Deposits Paid', value: stats.deposits_paid, icon: CheckCircle2, color: 'emerald', change: '+8%' },
    { label: 'Pending Deposits', value: stats.pending_deposits, icon: Clock, color: 'amber', change: '-3%' },
    { label: 'Pickups Scheduled', value: stats.pickups_scheduled, icon: Truck, color: 'purple' },
    { label: 'Pickups Completed', value: stats.pickups_completed, icon: CheckCircle2, color: 'emerald' },
    { label: 'Deliveries Scheduled', value: stats.deliveries_scheduled, icon: Package, color: 'cyan' },
    { label: 'Inventory Items', value: stats.total_items, icon: Package, color: 'indigo' },
    { label: 'Revenue', value: `$${stats.revenue.toLocaleString()}`, icon: DollarSign, color: 'emerald', change: '+15%' },
    { label: 'Outstanding', value: `$${stats.outstanding_balances.toLocaleString()}`, icon: DollarSign, color: 'red' },
    { label: 'Active Schools', value: stats.active_schools, icon: GraduationCap, color: 'blue' },
    { label: 'Conversion Rate', value: `${stats.conversion_rate}%`, icon: TrendingUp, color: 'emerald', change: '+2.4%' },
  ];

  const schoolChartData = Object.entries(stats.reservations_by_school || {}).map(([name, count]) => ({
    name: name.length > 15 ? name.substring(0, 15) + '...' : name, count
  })).sort((a, b) => b.count - a.count);

  const pieData = [
    { name: 'Paid', value: stats.deposits_paid, color: '#10b981' },
    { name: 'Pending', value: stats.pending_deposits, color: '#f59e0b' },
  ];

  const trendData = [
    { week: 'W1', reservations: 3, revenue: 90 },
    { week: 'W2', reservations: 5, revenue: 150 },
    { week: 'W3', reservations: 8, revenue: 240 },
    { week: 'W4', reservations: 6, revenue: 180 },
    { week: 'W5', reservations: 12, revenue: 360 },
    { week: 'W6', reservations: 15, revenue: 450 },
    { week: 'W7', reservations: 18, revenue: 540 },
    { week: 'W8', reservations: 25, revenue: 750 },
  ];

  const colorMap = { blue: '#3b82f6', emerald: '#10b981', amber: '#f59e0b', purple: '#8b5cf6', cyan: '#06b6d4', indigo: '#6366f1', red: '#ef4444', violet: '#8b5cf6' };

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Executive Dashboard</h1>
        <p className={`text-sm ${ctx.textSecondary} mt-1`}>Overview of Student Store It operations</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6" data-testid="kpi-grid">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className={`${ctx.cardBg} rounded-xl p-4 border ${ctx.borderColor}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ background: `${colorMap[kpi.color]}15` }}>
                  <Icon className="w-4 h-4" style={{ color: colorMap[kpi.color] }} />
                </div>
                {kpi.change && (
                  <span className={`text-xs font-medium ${kpi.change.startsWith('+') ? 'text-emerald-400' : 'text-red-400'}`}>
                    {kpi.change}
                  </span>
                )}
              </div>
              <p className="text-xl font-bold">{kpi.value}</p>
              <p className={`text-xs ${ctx.textSecondary} mt-0.5`}>{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Revenue Trend */}
        <div className={`${ctx.cardBg} rounded-xl p-5 border ${ctx.borderColor} lg:col-span-2`}>
          <h3 className="text-sm font-semibold mb-4">Weekly Growth</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="reservations" stroke="#3b82f6" fill="url(#gBlue)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Deposit Pie */}
        <div className={`${ctx.cardBg} rounded-xl p-5 border ${ctx.borderColor}`}>
          <h3 className="text-sm font-semibold mb-4">Deposit Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={4} dataKey="value">
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {pieData.map(d => (
              <span key={d.name} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Reservations by School */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className={`${ctx.cardBg} rounded-xl p-5 border ${ctx.borderColor}`}>
          <h3 className="text-sm font-semibold mb-4">Reservations by School</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={schoolChartData} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#6b7280' }} width={130} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e2030', border: 'none', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Reservations */}
        <div className={`${ctx.cardBg} rounded-xl p-5 border ${ctx.borderColor}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Recent Reservations</h3>
            <Link to="/admin/reservations" className="text-xs text-blue-400 hover:underline flex items-center gap-1">
              View All <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {(stats.recent_reservations || []).map((r, i) => (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${ctx.borderColor}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.student_name}</p>
                  <p className={`text-xs ${ctx.textSecondary}`}>{r.school}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${r.deposit_paid ? STATUS_COLORS.Paid : STATUS_COLORS.Pending}`}>
                    {r.deposit_paid ? 'Paid' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Operational Alerts */}
      <div className={`${ctx.cardBg} rounded-xl p-5 border ${ctx.borderColor}`}>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" /> Operational Alerts
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
            <p className="text-xs text-amber-400 font-medium">{stats.pending_deposits} pending deposits</p>
            <p className={`text-xs ${ctx.textSecondary} mt-1`}>Follow up with students</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
            <p className="text-xs text-blue-400 font-medium">{stats.pickups_scheduled} pickups upcoming</p>
            <p className={`text-xs ${ctx.textSecondary} mt-1`}>Ensure mover availability</p>
          </div>
          <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
            <p className="text-xs text-red-400 font-medium">${stats.outstanding_balances} outstanding</p>
            <p className={`text-xs ${ctx.textSecondary} mt-1`}>Send payment reminders</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
            <p className="text-xs text-emerald-400 font-medium">{stats.conversion_rate}% conversion</p>
            <p className={`text-xs ${ctx.textSecondary} mt-1`}>Above target of 70%</p>
          </div>
        </div>
      </div>
    </div>
  );
}
